import { Flex, FlexProps, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

export interface IOption {
  label: string
  value: string | number
}
interface IBbSwitchGeneral<T> extends FlexProps {
  option1: T
  option2: T
  initialValue?: T
  /**
   * Pass this to run the switch as a controlled component. The selected option then comes
   * from the parent and `onToggle` fires only on an actual click - unlike the uncontrolled
   * mode, which also fires it once on mount.
   */
  value?: T
  size: 'sm' | 'md' | 'lg'
  onToggle: (value: T) => void
}

const BbSwitchGeneral = <T extends IOption>(props: IBbSwitchGeneral<T>) => {
  const { option1, option2, initialValue, value, onToggle, size, ...rest } = props

  const isControlled = value !== undefined
  const [internalSelected, setInternalSelected] = useState(initialValue ?? option1)
  const currentSelected = value ?? internalSelected

  useEffect(() => {
    if (!isControlled) {
      onToggle(internalSelected)
    }
  }, [internalSelected])

  const selectHandler = (option: T) => {
    if (option.value === currentSelected.value) return
    if (isControlled) {
      onToggle(option)
    } else {
      setInternalSelected(option)
    }
  }

  return (
    <Flex
      role='switch'
      width='fit-content'
      alignItems='center'
      cursor='pointer'
      userSelect='none'
      {...rest}
    >
      <Text
        bgColor={option1.value === currentSelected.value ? 'primary.200' : 'white'}
        color={option1.value === currentSelected.value ? 'primary.800' : 'black'}
        minWidth='1.5rem'
        paddingY='2'
        paddingX='6'
        border='solid 1px gray'
        borderLeftRadius='md'
        borderColor='primary.600'
        onClick={() => selectHandler(option1)}
        fontSize={size}
      >
        {option1.label}
      </Text>
      <Text
        bgColor={option1.value !== currentSelected.value ? 'primary.200' : 'white'}
        color={option1.value !== currentSelected.value ? 'primary.800' : 'black'}
        minWidth='1.5rem'
        paddingY='2'
        paddingX='6'
        border='solid 1px gray'
        borderLeft='none'
        borderRightRadius='md'
        borderColor='primary.600'
        onClick={() => selectHandler(option2)}
        fontSize={size}
      >
        {option2.label}
      </Text>
    </Flex>
  )
}

export { BbSwitchGeneral }
